import React, { useContext } from "react";
import OverallJourneyListingDtoStatus from "../../../types/dashboard/manager/OverallJourneyListingDtoStatus";
import JourneyListingDto from "../../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import cx from "classnames";
import {
  faClock,
  faCheckCircle,
  faCalendarAlt,
  faHourglassHalf,
} from "@fortawesome/pro-regular-svg-icons";
import { t } from "i18next";
import UserContext from "../../../state/UserContext";

interface StatisticsBarProps {
  journeys: JourneyListingDto[] | null;
  originalJourneys: JourneyListingDto[] | null;
  hasUpcomingJourneys: boolean | null;
  formType: string;
  selectedStatus: string;
  searchTerm: string | null;
  selectedDateRange: string;
  onStatusChange: (status: string) => void;
}

const JourneyStatisticsBar: React.FC<StatisticsBarProps> = ({
  journeys,
  originalJourneys,
  hasUpcomingJourneys,
  formType,
  selectedStatus,
  searchTerm,
  selectedDateRange,
  onStatusChange,
}) => {
  const userContext = useContext(UserContext);

  // Helper function to get the relevant date for filtering
  const getJourneyFilterDate = (journey: JourneyListingDto): Date | null => {
    // Use last modified date if it exists, otherwise use send date
    if (journey.dateLastUpdated) {
      return new Date(journey.dateLastUpdated);
    }
    if (journey.dateSent) {
      return new Date(journey.dateSent);
    }
    return null;
  };

  // Helper function to check if a journey falls within the selected date range
  const isWithinDateRange = (
    journey: JourneyListingDto,
    dateRange: string
  ): boolean => {
    // Always show "Upcoming" journeys regardless of date filter
    if (journey.overallStatus === "UPCOMING") {
      return true;
    }

    const journeyDate = getJourneyFilterDate(journey);
    if (!journeyDate) return false;

    const now = new Date();
    let cutoffDate: Date;

    switch (dateRange) {
      case "Last six months":
        cutoffDate = new Date();
        cutoffDate.setMonth(now.getMonth() - 6);
        break;
      case "In the last year":
        cutoffDate = new Date();
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      case "All time":
        return true; // No date filtering for "All time"
      default:
        return true;
    }

    return journeyDate >= cutoffDate;
  };

  // Calculate counts from the filtered journeys
  const calculateStatusCounts = (
    journeyList: JourneyListingDto[] | null,
    applyDateFilter: boolean = false
  ): Record<string, number> => {
    if (!journeyList) return {};

    const filteredJourneys = applyDateFilter
      ? journeyList.filter((journey) =>
          isWithinDateRange(journey, selectedDateRange)
        )
      : journeyList;

    return filteredJourneys.reduce(
      (acc: Record<string, number>, journey) => {
        const { overallStatus } = journey;
        if (overallStatus !== "UNKNOWN") {
          acc[overallStatus] = (acc[overallStatus] || 0) + 1;
        }
        return acc;
      },
      { Upcoming: hasUpcomingJourneys ? 1 : 0 }
    );
  };

  // Get counts for filtered and original journeys
  const filteredCounts = calculateStatusCounts(journeys);
  const totalCounts = calculateStatusCounts(originalJourneys, true); // Apply date filter to total counts

  // Add helper function to determine filter type
  const getFilterType = () => {
    const isStatusFiltered = selectedStatus !== "All Statuses";
    const isSearchFiltered = searchTerm !== null && searchTerm.trim() !== "";

    if (isStatusFiltered && isSearchFiltered) {
      return "both";
    } else if (isStatusFiltered) {
      return "status";
    } else if (isSearchFiltered) {
      return "search";
    }
    return "none";
  };

  // Determine if we're filtering (so we show both numbers)
  const filterType = getFilterType();
  const isFiltering = filterType !== "none";

  const getLabel = (status: OverallJourneyListingDtoStatus): string => {
    switch (status) {
      case "NOT-STARTED":
        return `Not Started`;
      case "IN-PROGRESS":
        return `In Progress`;
      case "COMPLETED":
        return `Completed`;
      case "UPCOMING":
        return `Upcoming`;
      default:
        return status;
    }
  };

  const orderedStatuses: OverallJourneyListingDtoStatus[] = [
    "NOT-STARTED",
    "IN-PROGRESS",
    "COMPLETED",
    ...(hasUpcomingJourneys
      ? ["UPCOMING" as OverallJourneyListingDtoStatus]
      : []),
  ];

  // Handle status card click to toggle filtering
  const handleStatusClick = (status: OverallJourneyListingDtoStatus) => {
    // If already selected, reset to "All Statuses", otherwise apply the filter
    if (selectedStatus === getLabel(status)) {
      onStatusChange("All Statuses");
    } else {
      onStatusChange(getLabel(status));
    }
  };

  return (
    <div
      className={cx(
        "statistics-bar grid gap-2 sm:gap-4 py-4 sm:py-6",
        hasUpcomingJourneys
          ? "grid-cols-2 sm:grid-cols-4"
          : "grid-cols-2 sm:grid-cols-3"
      )}
    >
      {orderedStatuses.map((status) => {
        const isSelected = selectedStatus === getLabel(status);
        const filteredCount = filteredCounts[status] || 0;
        const totalCount = totalCounts[status] || 0;

        return (
          <div
            key={status}
            onClick={() => handleStatusClick(status)}
            className={cx(
              "stat-item bg-white p-2 sm:p-4 rounded-lg shadow-sm border flex justify-between items-center relative cursor-pointer transition-all",
              isSelected
                ? "primary-text font-semibold border primary-border"
                : "border-gray-200 text-gray-700 hover:shadow-md hover:border-gray-300"
            )}
            style={
              isSelected
                ? {
                    backgroundColor: `#${userContext?.user?.client.primaryHexColour}10`,
                  }
                : {}
            }
          >
            <div
              className={cx(
                "absolute left-0 top-0 bottom-0 w-1 rounded-tl-lg rounded-bl-lg primary-bg"
              )}
            ></div>
            <div className="flex items-center">
              <p className="font-medium text-sm sm:text-lg pl-2">
                {getLabel(status)}
              </p>
            </div>
            <div className="text-right">
              {isFiltering ? (
                // Show both filtered and total counts when filtering (both for selected and non-selected cards)
                <div>
                  <p className="text-lg sm:text-xl font-semibold flex items-center justify-end">
                    <span
                      className={`${
                        isSelected ? "primary-text font-bold" : "primary-text"
                      } mr-1 text-sm sm:text-base`}
                    >
                      {filteredCount}
                    </span>
                    <span
                      className={`${
                        isSelected ? "primary-text" : "primary-text"
                      } mr-1 text-xs sm:text-sm`}
                    >
                      /
                    </span>
                    <span className={isSelected ? "primary-text" : ""}>
                      {totalCount}
                    </span>
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {formType === "CONVERSATIONS"
                      ? t("Nav.LargeScreen.ManagerDashboard.Conversations")
                      : t("Nav.LargeScreen.ManagerDashboard.PulseChecks")}
                  </p>
                </div>
              ) : (
                // Show only one number when not filtering
                <div>
                  <p className="text-lg sm:text-xl font-semibold">
                    {totalCount}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {formType === "CONVERSATIONS"
                      ? t("Nav.LargeScreen.ManagerDashboard.Conversations")
                      : t("Nav.LargeScreen.ManagerDashboard.PulseChecks")}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default JourneyStatisticsBar;
